import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Floating label rendered above the input. */
  label?: ReactNode;
  /** Helper hint shown below the input when there is no error. */
  helperText?: ReactNode;
  /** When true, the input + helper render in red. */
  error?: boolean;
  /** Replaces helperText when `error` is true. */
  errorMessage?: ReactNode;
}

/**
 * Floating-label text input lifted from My.Tms.Old/components/TextInput.tsx
 * with the irrelevant `onDelete` knob trimmed (rare; can be re-added once a
 * screen actually needs it). Uses the shared `cn()` so callers can override
 * with extra Tailwind classes via the `className` prop.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, helperText, error = false, errorMessage, id, className, ...props },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? props.name ?? reactId;

  return (
    <div className="w-full">
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          placeholder={props.placeholder ?? ' '}
          className={cn(
            'peer h-9 w-full rounded-sm border bg-white px-2 pb-1 pt-2 text-sm font-semibold text-slate-900',
            // Placeholder visual: smaller, italic, normal-weight, muted —
            // visually distinct from real input text (which is text-sm
            // semibold slate-900) so it reads as a hint rather than data.
            'placeholder:text-xs placeholder:font-normal placeholder:italic placeholder:text-slate-400',
            'focus:outline-none focus:ring-2',
            // Soft disabled / read-only look — `bg-slate-50` instead of the
            // default browser gray (or a hand-rolled slate-100) so the
            // floating label sits comfortably against the field. Both
            // attributes share the same look since they're functionally
            // equivalent for the operator (can't edit the value).
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
            'read-only:cursor-not-allowed read-only:bg-slate-50 read-only:text-slate-500',
            error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500',
            className,
          )}
          {...props}
        />
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500',
              error && 'peer-focus:text-red-500',
            )}
          >
            {label}
          </label>
        )}
      </div>
      {(helperText || (error && errorMessage)) && (
        <p className={cn('mt-1 text-xs', error ? 'text-red-600' : 'text-slate-500')}>
          {error ? errorMessage : helperText}
        </p>
      )}
    </div>
  );
});
